import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchPrerequisiteCandidates, type PrerequisiteCandidate } from '../api/client.js';
import { resolveEntityTitles } from '../lib/entityTitles.js';
import { buildPrerequisiteReviewerPatch, proposedPrerequisites, readPrerequisiteTuple } from '../lib/prerequisitePatch.js';

/**
 * THE ONLY SCREEN IN THIS PRODUCT ON WHICH A LESSON'S PREREQUISITES CAN BE TYPED IN.
 *
 * Before this, a reviewer facing an item that asks *"what should this lesson follow?"* had exactly
 * two actions — approve as proposed, and reject. The queue drawer's edit mode rendered one input
 * bound to `title` and disabled its button without one, so a prerequisite item could never submit
 * an edit at all; the structural screen disables Approve with Edit outright. The answer therefore
 * came back through Slack and we wired it by hand.
 *
 * Deliberately NOT a graph editor. It edits one lesson's outgoing edges, as chips, and that is all.
 *
 * The reviewer is a CFA subject-matter expert, not an engineer: he thinks in lesson titles, so
 * every id is rendered with its title resolved and the picker searches by title.
 */
export function PrerequisiteEditor({
  courseId,
  disabled = false,
  entityId,
  onDiscard,
  onSubmit,
  patch,
}: {
  courseId: string;
  disabled?: boolean;
  entityId: number | null;
  onDiscard: () => void;
  onSubmit: (reviewerPatch: Record<string, unknown>) => void;
  patch: Record<string, unknown>;
}) {
  const tuple = readPrerequisiteTuple(patch);
  const proposed = useMemo(() => proposedPrerequisites(patch), [patch]);
  const reviewedBefore = useMemo(
    () => (tuple && tuple.before && typeof tuple.before === 'object' && !Array.isArray(tuple.before)
      ? (tuple.before as Record<string, unknown>)
      : {}),
    [tuple],
  );

  // The draft starts from what the item PROPOSES, not from what the lesson currently says: the
  // reviewer is amending a proposal, and starting anywhere else would silently discard it.
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({ ...proposed }));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PrerequisiteCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [titles, setTitles] = useState<Record<number, string>>({});

  const draftKeys = Object.keys(draft);
  const titleKey = draftKeys.join(',');

  useEffect(() => {
    let cancelled = false;
    const ids = draftKeys.map((key) => Number(key)).filter((id) => Number.isFinite(id));
    if (ids.length === 0) {
      return;
    }
    void resolveEntityTitles(courseId, ids).then((resolved) => {
      if (!cancelled) {
        setTitles((current) => ({ ...current, ...resolved }));
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, titleKey]);

  // Debounced so a search does not fire per keystroke. The endpoint reads the course's entity
  // documents to match without a regex, so it is not free.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      void fetchPrerequisiteCandidates(courseId, term, controller.signal)
        .then((found) => {
          if (!controller.signal.aborted) {
            setResults(found);
            setSearching(false);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([]);
            setSearching(false);
          }
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [courseId, query]);

  const labelFor = (key: string) => {
    const id = Number(key);
    return (Number.isFinite(id) ? titles[id] : undefined) || `Entity ${key}`;
  };

  const addCandidate = (candidate: PrerequisiteCandidate) => {
    const key = String(candidate.id);
    setDraft((current) => {
      if (Object.prototype.hasOwnProperty.call(current, key)) {
        return current;
      }
      // A KEPT edge keeps its stored weight; a new one gets 1. The map's values are `prereq_theta`
      // and writing the field replaces all of it, so re-adding an edge at the default weight would
      // silently overwrite whatever it was set to.
      const stored = Object.prototype.hasOwnProperty.call(reviewedBefore, key) ? reviewedBefore[key] : 1;
      return { ...current, [key]: stored };
    });
    setTitles((current) => ({ ...current, [candidate.id]: candidate.title }));
    setQuery('');
    setResults(null);
  };

  const removeKey = (key: string) => {
    setDraft((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const unchanged = sameMap(draft, proposed);
  const beforeKeys = new Set(Object.keys(reviewedBefore));

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="prerequisite-editor">
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-bold uppercase text-slate-500">
          Set what this lesson requires, then approve
        </span>
        <span className="text-[11px] text-slate-500">
          A learner must finish everything listed here before this lesson opens.
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="prerequisite-editor-chips">
        {draftKeys.length === 0 ? (
          <span className="text-xs text-slate-500">
            Requires nothing — this lesson opens its module.
          </span>
        ) : (
          draftKeys.map((key) => (
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                beforeKeys.has(key)
                  ? 'border-slate-200 bg-white text-slate-600'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}
              key={key}
            >
              {labelFor(key)}
              <span className="font-mono text-[9px] opacity-50">#{key}</span>
              <button
                aria-label={`Remove ${labelFor(key)}`}
                className="ml-0.5 rounded-full px-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                onClick={() => removeKey(key)}
                type="button"
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      {/* A prerequisite this lesson had and the draft no longer does. Named, because a deleted edge
          is invisible once the chip is gone and Brain2's drop cascades. */}
      {[...beforeKeys].filter((key) => !Object.prototype.hasOwnProperty.call(draft, key)).length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-400">Removing</span>
          {[...beforeKeys]
            .filter((key) => !Object.prototype.hasOwnProperty.call(draft, key))
            .map((key) => (
              <button
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 line-through hover:no-underline"
                key={key}
                onClick={() => setDraft((current) => ({ ...current, [key]: reviewedBefore[key] }))}
                title="Put it back"
                type="button"
              >
                {labelFor(key)}
                <span className="font-mono text-[9px] opacity-50">#{key}</span>
              </button>
            ))}
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <input
          aria-label="Search lessons to require"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search lessons by title or id…"
          type="text"
          value={query}
        />
        {searching ? <span className="text-[11px] text-slate-400">Searching…</span> : null}
        {results && results.length === 0 && !searching ? (
          <span className="text-[11px] text-slate-400">No live lesson matches that.</span>
        ) : null}
        {results && results.length > 0 ? (
          <ul className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-white">
            {results.map((candidate) => (
              <li key={candidate.id}>
                <button
                  className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs hover:bg-slate-50"
                  disabled={Object.prototype.hasOwnProperty.call(draft, String(candidate.id))}
                  onClick={() => addCandidate(candidate)}
                  type="button"
                >
                  <span className="truncate">
                    {candidate.title} <span className="font-mono text-[9px] text-slate-400">#{candidate.id}</span>
                  </span>
                  {/* Legal to point at, and it does NOT count: Brain2 drops a published lesson whose
                      prerequisite is unpublished. Offering it silently would let a reviewer create
                      tomorrow's orphan while fixing today's. */}
                  {candidate.published ? null : (
                    <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700">
                      not published
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {tuple ? null : (
        <span className="text-[11px] text-amber-700">
          This item does not record what the lesson said when it was drafted, so the write cannot
          check that nothing has changed since.
        </span>
      )}

      <div className="flex items-center gap-2">
        <button
          className="flex-1 rounded bg-primary py-2 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-40"
          disabled={disabled || unchanged}
          onClick={() => onSubmit(buildPrerequisiteReviewerPatch(patch, draft))}
          title={unchanged ? 'Nothing changed yet — use Approve to accept the proposal as it stands' : undefined}
          type="button"
        >
          Save &amp; approve
        </button>
        <button
          className="rounded border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50"
          onClick={onDiscard}
          type="button"
        >
          Discard
        </button>
      </div>
      {entityId == null ? null : (
        <span className="text-[10px] text-slate-400">Editing lesson #{entityId}</span>
      )}
    </div>
  );
}

/** Key sets and values, compared exactly — the same reading the executor's drift check uses. */
function sameMap(left: Record<string, unknown>, right: Record<string, unknown>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length
    && leftKeys.every(
      (key) => Object.prototype.hasOwnProperty.call(right, key) && Object.is(left[key], right[key]),
    )
  );
}
